import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Alert,
  Paper,
  Tabs,
  Tab,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AnswerForm from './AnswerForm';
import DocumentPreview from './DocumentPreview';
import { isStaticTemplate } from '../../utils/templateKeyUtils';


const PreviewTab = ({
  content,
  questions = [],
  questionTree = null
}) => {
  const [answers, setAnswers] = useState({});
  const [mobileTab, setMobileTab] = useState(0);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isStatic = useMemo(() => {
    return isStaticTemplate(content || '');
  }, [content]);

  const handleAnswerChange = (newAnswers) => {
    setAnswers(newAnswers);
  };

  const handleClearAnswers = () => {
    setAnswers({});
  };

  const handleMobileTabChange = (event, newValue) => {
    setMobileTab(newValue);
  };

  // Static template
  if (isStatic) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Bu şablon statik içerik. Hiç {'{{key}}'} bulunmadığı için soru formu yok.
        </Alert>
        <DocumentPreview
          content={content}
          answers={{}}
          questions={questions}
          questionTree={questionTree}
          showOptionalPlaceholders={true}
        />
      </Box>
    );
  }

  // Mobile (tab)
  if (isMobile) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Tabs
            value={mobileTab}
            onChange={handleMobileTabChange}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab label="Cevaplar" />
            <Tab label="Önizleme" />
          </Tabs>
        </Box>
        
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {mobileTab === 0 ? (
            <Paper sx={{ 
              p: 2, 
              height: '100%', 
              width: '100%',
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}>
              <AnswerForm
                questions={questions}
                questionTree={questionTree}
                answers={answers}
                onChange={handleAnswerChange}
                onClear={handleClearAnswers}
             />
            </Paper>
          ) : (
            <Box 
              sx={{ 
                height: '100%', 
                overflow: 'auto',
                // Modern ince scrollbar - Webkit (Chrome, Safari, Edge)
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "3px",
                  transition: "background 0.2s ease",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.35)",
                  },
                },
                // Modern ince scrollbar - Firefox
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
              }}>
              <DocumentPreview
                content={content}
                answers={answers}
                questions={questions}
                questionTree={questionTree}
                showOptionalPlaceholders={true}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Desktop (split)
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: '100%' }}>
        {/* Left: Answer Form */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
          <Paper sx={{ 
            p: 2, 
            height: '100%', 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0, 
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}>
            <AnswerForm
              questions={questions}
              questionTree={questionTree}
              answers={answers}
              onChange={handleAnswerChange}
              onClear={handleClearAnswers}
            />
          </Paper>
        </Grid>
        
        {/* Right: Document Preview */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box 
            sx={{ 
              height: '100%', 
              overflow: 'auto', 
              minHeight: 0,
              // Modern ince scrollbar - Webkit (Chrome, Safari, Edge)
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0, 0, 0, 0.2)",
                borderRadius: "3px",
                transition: "background 0.2s ease",
                "&:hover": {
                  background: "rgba(0, 0, 0, 0.35)",
                },
              },
              // Modern ince scrollbar - Firefox
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
            }}>
            <DocumentPreview
              content={content}
              answers={answers}
              questions={questions}
              questionTree={questionTree}
              showOptionalPlaceholders={true}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

PreviewTab.propTypes = {
  content: PropTypes.string,
  questions: PropTypes.array,
  questionTree: PropTypes.array,
  showProgressBar: PropTypes.bool,
  useWizardForm: PropTypes.bool,
};

PreviewTab.defaultProps = {
  content: '',
  questions: [],
  questionTree: null,
  showProgressBar: false,
  useWizardForm: false,
};

export default PreviewTab;
